// benefit-bundle-storefront 后台编排 API (组4).
//
// 后端 /admin/v1/benefit/* (admin 鉴权):
//   products:  GET/POST /products, POST /products/image(multipart), PUT /products/:id
//   bundles:   GET/POST /bundles, GET/PUT /bundles/:id
//   slots:     POST /bundles/:id/slots, DELETE /slots/:id
//   candidates:POST /slots/:id/candidates, DELETE /candidates/:id

import axios from 'axios';
import { env } from '@/utils/env';
import { useAuthStore } from '@/stores/auth';
import { get, list, post, put, del, type ListResult } from './client';

export type FulfillType = 'membership' | 'topup' | 'cardkey' | 'image';
export type BundleStatus = 'draft' | 'active' | 'inactive';
export type SlotKind = 'fixed' | 'choice';

export const FULFILL_LABEL: Record<FulfillType, string> = {
  membership: '会员(自营)',
  topup: '直充',
  cardkey: '卡密',
  image: '图片型(海报)',
};

// ---------------------------------------------------------------------------
// 产品 benefit_products
// ---------------------------------------------------------------------------

export interface BenefitProduct {
  id: number;
  app_id: number;
  fulfill_type: FulfillType;
  name: string;
  /** image → {image_url,title}; membership/topup/cardkey → {sku_code,duration_days?} */
  config: Record<string, unknown>;
  cost_cents: number;
  enabled: boolean;
  created_at: string;
}

export function listBenefitProducts(appId: number, fulfillType?: FulfillType): Promise<ListResult<BenefitProduct>> {
  return list<BenefitProduct>('/admin/v1/benefit/products', {
    app_id: appId,
    ...(fulfillType ? { fulfill_type: fulfillType } : {}),
  });
}

export interface CreateProductInput {
  app_id: number;
  fulfill_type: Exclude<FulfillType, 'image'>;
  name: string;
  config: Record<string, unknown>;
  cost_cents: number;
}

export function createBenefitProduct(input: CreateProductInput): Promise<BenefitProduct> {
  return post<BenefitProduct>('/admin/v1/benefit/products', input);
}

export function updateBenefitProduct(
  id: number,
  input: Partial<Pick<BenefitProduct, 'name' | 'config' | 'cost_cents' | 'enabled'>>,
): Promise<BenefitProduct> {
  return put<BenefitProduct>(`/admin/v1/benefit/products/${id}`, input);
}

/**
 * 图片型一键建品: 上传含小程序码的海报图 → OSS, 后端建 image 产品并回。
 * 裸 axios + FormData (共享 client 会强设 application/json 盖掉 multipart boundary),
 * 与 uploads.ts 同款。
 */
export async function createImageProduct(
  file: File,
  meta: { app_id: number; title: string; cost_cents: number },
): Promise<BenefitProduct> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('app_id', String(meta.app_id));
  fd.append('title', meta.title);
  fd.append('cost_cents', String(meta.cost_cents));

  const token = useAuthStore.getState().token;
  const res = await axios.post(`${env.apiBase || ''}/admin/v1/benefit/products/image`, fd, {
    headers: token
      ? { 'X-Touliu-Admin-Token': token, Authorization: `Bearer ${token}` }
      : undefined,
    timeout: 60_000,
  });
  const body = res.data;
  const product: BenefitProduct | undefined = body?.data ?? body;
  if (!product?.id) {
    throw new Error('上传成功但未返回 product, 请检查后端 /admin/v1/benefit/products/image 返回结构');
  }
  return product;
}

// ---------------------------------------------------------------------------
// bundle + 槽 + 候选
// ---------------------------------------------------------------------------

export interface Bundle {
  id: number;
  name: string;
  slug?: string | null;
  monthly_price_cents: number;
  status: BundleStatus;
  created_at: string;
}

export interface BundleSlotCandidate {
  id: number;
  product_id: number;
  product_name: string;
  fulfill_type: FulfillType;
  cost_cents: number;
  sort_order: number;
}

export interface BundleSlot {
  id: number;
  kind: SlotKind;
  name: string;
  select_count: number;
  unselected_policy: string;
  reminder_after_hours: number;
  expire_after_hours: number;
  sort_order: number;
  candidates: BundleSlotCandidate[];
}

export interface BundleTree extends Bundle {
  app_id: number;
  slots: BundleSlot[];
}

export function listBundles(appId: number): Promise<ListResult<Bundle>> {
  return list<Bundle>('/admin/v1/benefit/bundles', { app_id: appId });
}

// 后端整树是嵌套形状 {bundle, slots:[{slot, candidates:[{candidate_id,product_id,
// sort_order, product:{...}}]}]}. 这里归一成扁平 BundleTree(candidate 提取 product
// 明细), 让页面按单一扁平模型渲染.
interface RawTree {
  bundle: BundleTree;
  slots: Array<{
    slot: Omit<BundleSlot, 'candidates'>;
    candidates: Array<{
      candidate_id: number;
      product_id: number;
      sort_order: number;
      product: { name: string; fulfill_type: FulfillType; cost_cents: number };
    }>;
  }>;
}

export async function getBundle(id: number): Promise<BundleTree> {
  const raw = await get<RawTree>(`/admin/v1/benefit/bundles/${id}`);
  return {
    ...raw.bundle,
    slots: (raw.slots ?? []).map((s) => ({
      ...s.slot,
      candidates: (s.candidates ?? []).map((c) => ({
        id: c.candidate_id,
        product_id: c.product_id,
        product_name: c.product?.name ?? '',
        fulfill_type: c.product?.fulfill_type,
        cost_cents: c.product?.cost_cents ?? 0,
        sort_order: c.sort_order,
      })),
    })),
  };
}

export function createBundle(input: {
  app_id: number;
  name: string;
  slug?: string;
  monthly_price_cents: number;
}): Promise<Bundle> {
  return post<Bundle>('/admin/v1/benefit/bundles', input);
}

export function updateBundle(
  id: number,
  input: Partial<{ name: string; slug: string; monthly_price_cents: number; status: BundleStatus }>,
): Promise<Bundle> {
  return put<Bundle>(`/admin/v1/benefit/bundles/${id}`, input);
}

export function addSlot(
  bundleId: number,
  input: {
    kind: SlotKind;
    name: string;
    select_count?: number;
    unselected_policy?: string;
    reminder_after_hours?: number;
    expire_after_hours?: number;
  },
): Promise<BundleSlot> {
  return post<BundleSlot>(`/admin/v1/benefit/bundles/${bundleId}/slots`, input);
}

export function deleteSlot(slotId: number): Promise<void> {
  return del(`/admin/v1/benefit/slots/${slotId}`);
}

export function addCandidate(slotId: number, productId: number): Promise<BundleSlotCandidate> {
  return post<BundleSlotCandidate>(`/admin/v1/benefit/slots/${slotId}/candidates`, { product_id: productId });
}

export function deleteCandidate(candidateId: number): Promise<void> {
  return del(`/admin/v1/benefit/candidates/${candidateId}`);
}

// 选择组"最贵候选成本"(design Risks: 候选成本极差, 按均值定价会亏) — 前端算给运营看.
export function maxCandidateCost(slot: BundleSlot): number {
  return slot.candidates.reduce((m, c) => Math.max(m, c.cost_cents), 0);
}

// ---------------------------------------------------------------------------
// bundle 订单 (对账/退款, phase1-promo 2026-08-13)
// ---------------------------------------------------------------------------

export interface BundleOrderRow {
  id: string;
  app_id: number;
  bundle_id: number;
  out_trade_no: string;
  phone: string;
  channel: string;
  amount_cents: number;
  status: 'pending' | 'paid' | 'failed' | 'refunded';
  /** 投放来源 (下单携带快手 callback) */
  from_ad: boolean;
  paid_at?: string | null;
  refunded_at?: string | null;
  created_at: string;
}

export function listBundleOrders(q: {
  app_id: number;
  status?: string;
  page?: number;
  page_size?: number;
}): Promise<ListResult<BundleOrderRow>> {
  return list<BundleOrderRow>('/admin/v1/benefit/orders', q);
}

/** 全额退款 (银商网关真实退钱, paid→refunded)。 */
export function refundBundleOrder(outTradeNo: string): Promise<BundleOrderRow> {
  return post<BundleOrderRow>(`/admin/v1/benefit/orders/${encodeURIComponent(outTradeNo)}/refund`, {});
}
